/**
 * ボム発動時の視覚エフェクトの状態更新および描画
 *
 * 背景のフラッシュ、複数の爆発、衝撃波などの演出を管理します。
 *
 * @module effects/bomb-effects
 */

import { getP5 } from '../../core/p5-instance';
import {
    getBombEffectTimer,
    getBombExplosions,
    getParticles,
    getPlayer,
    getDt,
    setBombEffectTimer,
    setIsBombActive,
    setBombExplosions
} from '../../store';

/**
 * ボムエフェクトの進行状態（タイマー・サイズ等）を更新します。
 * `dt` に基づいて各爆発のアニメーションを進めます。
 */
export function updateBombEffect(): void {
    const p = getP5();
    const dt = getDt();
    const bombEffectTimer = getBombEffectTimer();
    const bombExplosions = getBombExplosions();

    if (bombEffectTimer > 0) {
        // 全体の演出用タイマーを更新
        const nextTimer = bombEffectTimer - dt;
        setBombEffectTimer(nextTimer);

        // タイマー終了時にボム状態をリセット
        if (nextTimer <= 0) {
            setIsBombActive(false);
            setBombExplosions([]);
            return;
        }

        // 個別の爆発エフェクト（複数）の状態を更新
        for (const exp of bombExplosions) {
            // 各爆発の発生遅延（delay）を考慮し、タイミングが来たらアニメーション開始
            if (nextTimer <= 90 - exp.delay && exp.timer < 35) {
                exp.timer += dt;

                // 爆発のサイズ更新（サイン波を用いて 0 -> maxSize -> 0 と変化させる）
                const progress = p.constrain(exp.timer / 35, 0, 1);
                exp.size = exp.maxSize * p.sin(progress * p.PI);
            }
        }
    }
}

/**
 * ボムエフェクトを画面に描画します。
 * 注意: この関数は現在の状態に基づいて描画のみを行い、数値の更新は行いません。
 */
export function renderBombEffect(): void {
    const p = getP5();
    const bombEffectTimer = getBombEffectTimer();
    const bombExplosions = getBombExplosions();
    const particles = getParticles();

    if (bombEffectTimer <= 0) {
        return;
    }

    // --- 背景フラッシュ演出 ---
    // タイマーに応じてアルファ値を変化させ、画面を一瞬明るくする
    const flashAlpha = p.map(bombEffectTimer, 0, 90, 0, 120);
    p.fill(255, 150, 50, flashAlpha);
    p.rect(0, 0, p.width, p.height);

    // --- 個別の爆発エフェクトの描画 ---
    for (const exp of bombExplosions) {
        // 発生中（アニメーション期間内）のものだけ描画
        if (bombEffectTimer <= 90 - exp.delay && exp.timer < 35) {
            const progress = exp.timer / 35;

            // 1. 外側の爆発リング（2重の円で表現）
            p.noFill();
            p.stroke(exp.color);
            p.strokeWeight(10 - progress * 8); // 徐々に細くする
            p.circle(exp.x, exp.y, exp.size);

            p.stroke(255, 255, 200, 150 * (1 - progress));
            p.strokeWeight(4);
            p.circle(exp.x, exp.y, exp.size * 0.8);

            // 2. 内側の火球（グラデーション風の塗り）
            const innerSize = exp.size * 0.5;
            p.fill(255, 255, 200, 220 * (1 - progress));
            p.noStroke();
            p.circle(exp.x, exp.y, innerSize);

            p.fill(255, 200, 100, 180 * (1 - progress));
            p.circle(exp.x, exp.y, innerSize * 1.3);

            // 3. 火花パーティクルの追加放出
            // 爆発の開始から中盤にかけて、周囲に火花を散らす
            if (exp.timer % 2 < 1 && exp.timer < 20) {
                for (let i = 0; i < 5; i++) {
                    const angle = p.random(p.TWO_PI);
                    const speed = p.random(4, 12);
                    particles.push({
                        x: exp.x,
                        y: exp.y,
                        vx: p.cos(angle) * speed,
                        vy: p.sin(angle) * speed,
                        size: p.random(3, 10),
                        color: p.color(255, p.random(100, 255), p.random(0, 50)),
                        life: p.random(15, 30)
                    });
                }
            }
        }
    }

    // --- プレイヤー中心の衝撃波演出 ---
    const player = getPlayer();
    if (player) {
        // 第1波（発動直後）
        if (bombEffectTimer > 60) {
            const rippleProgress = (90 - bombEffectTimer) / 30;
            const rippleSize = rippleProgress * p.max(p.width, p.height) * 1.5;
            p.noFill();
            p.stroke(255, 200, 100, 180 * (1 - rippleProgress));
            p.strokeWeight(8);
            p.circle(player.x, player.y, rippleSize);
        }
        // 第2波（少し遅れて発生）
        if (bombEffectTimer > 45 && bombEffectTimer <= 75) {
            const rippleProgress = (75 - bombEffectTimer) / 30;
            const rippleSize = rippleProgress * p.max(p.width, p.height) * 1.3;
            p.noFill();
            p.stroke(255, 150, 50, 150 * (1 - rippleProgress));
            p.strokeWeight(5);
            p.circle(player.x, player.y, rippleSize);
        }
    }
}
